import { Button, Card, CardHeader, Checkbox, CircularProgress, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { makeStyles } from "@mui/styles";
import { useEffect } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectTypeCurves } from '../reducers/typeCurveSlice';

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
};

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
};

function union(a, b) {
  return [...a, ...not(b, a)];
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 0,
    width: '100%',
    position: 'relative'
  },
  listHeader: {
    position: 'fixed'
  },
  listContainer: {
    width: '42%',
  },
  list: {
    maxHeight: 300,
    minHeight: 300,
    overflow: 'auto'
  }
}));

export const ExportDataTypeCurvesTransferList = ({ typeCurveIds, setTypeCurveIds, isLoadingData }) => {
  const classes = useStyles();

  const typeCurves = useSelector(selectTypeCurves);
  
  const [checked, setChecked] = useState([]);

  const [left, setLeft] = useState(typeCurves.allIds);

  useEffect(() => {
    setLeft(not(typeCurves.allIds, typeCurveIds));
  }, [typeCurves, typeCurveIds]);

  const leftChecked = intersection(checked, left);
  const typeCurvesChecked = intersection(checked, typeCurveIds);


  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    };

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    };
  };

  const handleCheckedRight = () => {
    setTypeCurveIds(() => typeCurveIds.concat(leftChecked));
    setLeft(() => not(left, leftChecked));
    setChecked(() => not(checked, leftChecked));
  };

  const handleAllRight = () => {
    setTypeCurveIds(() => typeCurveIds.concat(left));
    setLeft([]);
  };

  const handleCheckedLeft = () => {
    setLeft(() => left.concat(typeCurvesChecked));
    setTypeCurveIds(() => not(typeCurveIds, typeCurvesChecked));
    setChecked(() => not(checked, typeCurvesChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(typeCurveIds));
    setTypeCurveIds([]);
  };

  const customList = (title, items) => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={
              numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            color="primary"
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      <Divider />
      <List
        dense
        component="div"
        role="list"
        className={classes.list}
      >
        {items.map((value) => {
          return (
            <ListItem
              key={value}
              role="listitem"
              button
              onClick={handleToggle(value)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  color="primary"
                />
              </ListItemIcon>
              <ListItemText primary={typeCurves?.byId?.[value]?.name} secondary={`${getTypeCurveLength(value, typeCurves) || ""} wells`} />
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Card>
  );
  
  try {
    return (
      <Grid container spacing={2} justifyContent="center" alignItems="center" className={classes.root}>
        {isLoadingData ? 
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: 400}}>
          <Typography style={{marginBottom: 20}}>Loading type curves...</Typography>
          <CircularProgress></CircularProgress>
        </div>
        :
        <>
        <Grid item className={classes.listContainer}>{customList('All Type Curves', left)}</Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleAllRight}
              disabled={left.length === 0}
            >
              ≫
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={typeCurvesChecked.length === 0}
            >
              &lt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleAllLeft}
              disabled={typeCurveIds.length === 0}
            >
              ≪
            </Button>
          </Grid>
        </Grid>
        <Grid item className={classes.listContainer}>{customList('Exported Type Curves', typeCurveIds)}</Grid>
        </>}
      </Grid>
    );
  } catch (error) {
    console.log(error);
    return (<></>);
  };
};

function getTypeCurveLength(typeCurveId, typeCurves) {
  return typeCurves?.byId?.[typeCurveId]?.uwis?.length || 0;
};